import type { CustomEffectsEvent } from '@entities/overlay-event'
import type { EffectComponentProps } from '@shared/lib/effect-registry'

import {
  isOverlayEffectSlotZone,
  OverlayEffectSlot,
  type OverlayEffectSlotZone,
} from '@shared/ui/OverlayEffectSlot'
import lottie, { type AnimationItem } from 'lottie-web'
import { useEffect, useRef } from 'react'

import styles from './CustomEffect.module.css'
import { getCachedLottie, setCachedLottie } from './lottieCache'

const FALLBACK_VIDEO_MS = 10_000
const FALLBACK_IMAGE_SEC = 5

const DEFAULT_ZONE: OverlayEffectSlotZone = 'fullscreen'

/**
 * custom_effects полностью перешёл на единую зонную модель — backend теперь
 * отдаёт в overlayConfig.zone сразу одно из 10 имён OverlayEffectSlotZone (см.
 * plugins/custom_effects/manifest.ts в backend, CUSTOM_EFFECTS_ZONE_ENUM_OPTIONS). Старый
 * угловой формат (corner-tl/tr/bl/br) больше не присылается и не поддерживается;
 * невалидное/отсутствующее значение откатывается на DEFAULT_ZONE.
 */
function toSlotZone(rawZone: string | undefined): OverlayEffectSlotZone {
  return isOverlayEffectSlotZone(rawZone) ? rawZone : DEFAULT_ZONE
}

/**
 * Расшифровка HTMLMediaElement.error.code — стандартные коды MediaError
 * (см. https://developer.mozilla.org/docs/Web/API/MediaError/code).
 * Нужна для диагностики в OBS/CEF, где обычные DevTools недоступны и
 * причину «чёрного экрана» видно только через удалённую CEF-инспекцию
 * (см. logEnvironment.ts) — без расшифровки в логе был бы только номер.
 */
const MEDIA_ERROR_CODE_NAMES: Record<number, string> = {
  1: 'MEDIA_ERR_ABORTED',
  2: 'MEDIA_ERR_NETWORK',
  3: 'MEDIA_ERR_DECODE',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED',
}

/**
 * Через сколько мс после монтирования считать, что видео «зависло» — src
 * задан, play() не отклонён явно, но воспроизведение так и не началось
 * (readyState всё ещё HAVE_NOTHING/HAVE_METADATA). Типичные причины:
 * presigned URL уже просрочен (сервер отвечает 403 без сетевой ошибки в
 * консоли), CORS блокирует чтение, либо кодек не поддерживается CEF.
 * Таймаут короче общего fallback (durationMs+1000), чтобы диагностика
 * успела залогироваться до автозавершения эффекта.
 */
const VIDEO_STALL_DIAGNOSTIC_MS = 4_000

/**
 * Убирает query-часть (presigned S3 URL содержит signature/expiry в
 * query, которые не должны попадать в логи даже браузерные) и возвращает
 * только pathname — этого достаточно для диагностики (какой ассет/формат
 * не загрузился), без утечки чувствительных параметров подписи.
 */
function sanitizeMediaUrlForLog(url: string): string {
  try {
    return new URL(url).pathname
  } catch {
    // Относительный URL или невалидная строка — отдаём как есть до ? (без query).
    return url.split('?')[0]
  }
}

/**
 * Логирует детальную причину сбоя <video> (код + текст MediaError, путь без
 * query-строки, readyState/networkState) через console.error — единственный канал
 * диагностики, доступный в OBS Browser Source без удалённого CEF-инспектора.
 * Раньше onError сразу дёргал onDone молча — эффект «пропадал» без единой
 * зацепки в логах, из-за чего причину «чёрного экрана» нельзя было понять
 * без доступа к сети/бэкенду.
 *
 * По результатам аудита безопасности: полный URL (включая presigned
 * query-параметры S3) больше не логируется — только pathname через
 * sanitizeMediaUrlForLog. Этого достаточно для диагностики и не раскрывает
 * временный доступ к объекту через логи.
 */
function logVideoFailure(video: HTMLVideoElement, context: string): void {
  const err = video.error
  const codeName = err ? (MEDIA_ERROR_CODE_NAMES[err.code] ?? `UNKNOWN(${err.code})`) : 'n/a'
  // eslint-disable-next-line no-console
  console.error('[overlay] custom_effects video failure', {
    context,
    src: sanitizeMediaUrlForLog(video.currentSrc || video.src),
    mediaErrorCode: codeName,
    mediaErrorMessage: err?.message || 'n/a',
    readyState: video.readyState,
    networkState: video.networkState,
  })
}

/**
 * Рендер одного пользовательского эффекта. Три ветки по kind:
 *  - video  — <video>, явный play().catch()→onDone (автоплей могут блокировать
 *             вне CEF) и onEnded→onDone, onError→onDone (+ страховочный таймер:
 *             CEF может не эмитить 'ended'). Диагностика: любая ошибка/зависание
 *             логируются через console.error ДО завершения эффекта (см.
 *             logVideoFailure, VIDEO_STALL_DIAGNOSTIC_MS).
 *  - lottie — lottie-web, renderer 'canvas' (дёшево для CEF без GPU), onComplete
 *             → onDone (+ тот же таймер). JSON кэшируется по effectAssetId.
 *  - image  — <img>, onError→onDone; завершение по таймеру displayDurationSecForImages.
 *
 * overlayConfig.zone задаёт зону компоновки — одно из 10 значений единой зонной
 * модели (fullscreen/center + 8 крайних зон); рендерится через общий OverlayEffectSlot
 * (тот же наслаивающийся слой, что и у donation_alert/fireworks/text_alert) без
 * ограничения allowedZones — все 10 зон теперь доступны через Studio UI
 * (см. CUSTOM_EFFECTS_ZONE_ENUM_OPTIONS в backend). При отсутствии/невалидности
 * zone — fullscreen (см. toSlotZone).
 *
 * Все ветки при unmount чистят таймер, lottie-инстанс и снимают сетевой запрос
 * с <video> — иначе при быстрой смене эффектов будут утечки и «фантомные» onDone.
 */
export function CustomEffectRender({ event, onDone }: EffectComponentProps<CustomEffectsEvent>) {
  const payload = event.payload
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)

  const slotZone = toSlotZone(payload.overlayConfig?.zone)

  useEffect(() => {
    const done = () => onDoneRef.current()
    const video = videoRef.current
    let anim: AnimationItem | null = null
    let cancelled = false

    let fallback: ReturnType<typeof setTimeout>
    if (payload.kind === 'image') {
      fallback = setTimeout(
        done,
        (payload.displayDurationSecForImages || FALLBACK_IMAGE_SEC) * 1_000,
      )
    } else {
      // video/lottie играют по своей длительности; +1с запас на буферизацию.
      fallback = setTimeout(done, (payload.durationMs ?? FALLBACK_VIDEO_MS) + 1_000)
    }

    let stallCheck: ReturnType<typeof setTimeout> | undefined

    if (payload.kind === 'video' && video) {
      // Важно: ставим src императивно здесь, а не только через JSX-атрибут.
      // Причина: в React 19 StrictMode (dev-режим) каждый компонент
      // монтируется дважды (mount → фиктивный unmount → mount) на одном
      // и том же DOM-узле. Cleanup первого (фиктивного) монтирования
      // вызывает video.removeAttribute('src') — React не восстанавливает атрибут
      // повторно на втором (настоящем) mount, так как virtual DOM не изменился
      // между рендерами — в результате <video> остаётся с пустым src навсегда,
      // пока не сменится eventId. Задаем src императивно на каждом вызове
      // этого эффекта — тогда второй (настоящий) mount гарантированно
      // восстановит корректный URL независимо от того, что сделал cleanup раньше.
      if (video.src !== payload.mediaUrl) {
        video.src = payload.mediaUrl
      }

      // Диагностический таймер: если через VIDEO_STALL_DIAGNOSTIC_MS видео так и
      // не начало реально воспроизводиться (readyState < HAVE_CURRENT_DATA и
      // currentTime всё ещё 0), логируем состояние — это «немой» сбой без
      // событий error/stalled (например, зависший CDN-ответ или бесконечный
      // pending на play() без явного reject).
      stallCheck = setTimeout(() => {
        if (cancelled) return
        if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA && video.currentTime === 0) {
          logVideoFailure(video, 'stalled: playback did not start in time')
        }
      }, VIDEO_STALL_DIAGNOSTIC_MS)

      // Явный автоплей: атрибута autoPlay недостаточно — со звуком автоплей
      // могут заблокировать вне CEF, тогда 'ended' не сработает. При отказе
      // сразу завершаем, не дожидаясь страховочного таймера. Optional chaining —
      // на случай окружения без Promise-возврата у play() (jsdom).
      video.play()?.catch((err: unknown) => {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.error('[overlay] custom_effects video play() rejected', {
          src: sanitizeMediaUrlForLog(payload.mediaUrl),
          error: err instanceof Error ? err.message : String(err),
        })
        done()
      })
    }

    if (payload.kind === 'lottie') {
      const container = containerRef.current
      if (!container) {
        done()
      } else {
        const startLottie = (animationData: unknown) => {
          if (cancelled || !containerRef.current) return
          anim = lottie.loadAnimation({
            animationData,
            autoplay: true,
            container: containerRef.current,
            loop: false,
            renderer: 'canvas',
          })
          anim.addEventListener('complete', done)
        }

        const cached = getCachedLottie(payload.effectAssetId)
        if (cached !== undefined) {
          startLottie(cached)
        } else {
          fetch(payload.mediaUrl)
            .then((r) => r.json())
            .then((animationData: unknown) => {
              if (cancelled) return
              setCachedLottie(payload.effectAssetId, animationData)
              startLottie(animationData)
            })
            .catch((err: unknown) => {
              // eslint-disable-next-line no-console
              console.error('[overlay] custom_effects lottie fetch failed', {
                src: sanitizeMediaUrlForLog(payload.mediaUrl),
                error: err instanceof Error ? err.message : String(err),
              })
              done()
            })
        }
      }
    }

    return () => {
      cancelled = true
      clearTimeout(fallback)
      if (stallCheck) clearTimeout(stallCheck)
      if (anim) anim.destroy()
      if (video) {
        video.pause()
        video.removeAttribute('src')
        video.load()
      }
    }
  }, [payload])

  if (payload.kind === 'video') {
    return (
      <OverlayEffectSlot zone={slotZone} data-testid="custom-effect-video">
        <video
          ref={videoRef}
          className={styles.media}
          muted={!payload.hasAudio}
          onEnded={() => onDoneRef.current()}
          onError={(e) => {
            logVideoFailure(e.currentTarget, 'onError event')
            onDoneRef.current()
          }}
          playsInline
          preload="auto"
          src={payload.mediaUrl}
        />
      </OverlayEffectSlot>
    )
  }

  if (payload.kind === 'image') {
    return (
      <OverlayEffectSlot zone={slotZone} data-testid="custom-effect-image">
        <img
          alt={payload.effectName}
          className={styles.media}
          onError={() => onDoneRef.current()}
          src={payload.mediaUrl}
        />
      </OverlayEffectSlot>
    )
  }

  // lottie — контейнер под canvas-рендерер
  return (
    <OverlayEffectSlot zone={slotZone} data-testid="custom-effect-lottie">
      <div ref={containerRef} className={styles.media} />
    </OverlayEffectSlot>
  )
}
